import React, { useMemo, useState } from "react";

// Per-question score heatmap. Collapsed by default. Rows are questions,
// columns are candidates; each cell shows the candidate's score for that
// question and is clickable to open the answer drilldown side panel.
//
// Data shape (from /api/candidate-comparison/compare → questionScores):
//   [{ questionId, questionText, competency, scoreByUser: {userId: score} }]
export default function QuestionScoresTable({ candidates, questionScores, onCellClick }) {
  const [open, setOpen] = useState(false);

  const rows = useMemo(() => Array.isArray(questionScores) ? questionScores : [], [questionScores]);

  if (rows.length === 0 || !candidates || candidates.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-200 hover:bg-slate-50"
      >
        <div className="text-left">
          <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">
            Question-level breakdown
          </div>
          <div className="text-[11px] text-vf-muted mt-0.5">
            {rows.length} question{rows.length === 1 ? "" : "s"} · click a cell for the answer
          </div>
        </div>
        <span className="text-xs text-vf-muted">{open ? "▼ Hide" : "▶ Show"}</span>
      </button>

      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-slate-50 z-10 text-left p-2 text-xs text-vf-muted uppercase font-semibold min-w-[280px]">
                  Question
                </th>
                <th className="p-2 text-xs text-vf-muted uppercase font-semibold text-left bg-slate-50 min-w-[120px]">
                  Competency
                </th>
                {candidates.map((c) => (
                  <th
                    key={c.userId}
                    className="p-2 text-xs text-vf-base font-semibold text-center bg-slate-50"
                    style={{ minWidth: 80 }}
                    title={c.fullName}
                  >
                    <div className="truncate max-w-[120px] mx-auto">{c.fullName}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((q) => (
                <tr key={q.questionId} className="border-t border-slate-100">
                  <td className="sticky left-0 bg-white z-10 p-2 text-xs text-vf-base border-r border-slate-100 align-top">
                    <div className="line-clamp-2" title={q.questionText}>{q.questionText || `#${q.questionId}`}</div>
                  </td>
                  <td className="p-2 text-xs text-vf-muted align-top">{q.competency || "—"}</td>
                  {candidates.map((c) => {
                    const raw = q.scoreByUser?.[c.userId];
                    const score = raw == null ? null : Number(raw);
                    return (
                      <td key={c.userId} className="p-0 text-center align-top">
                        <Cell
                          score={score}
                          onClick={() => onCellClick && onCellClick({ candidate: c, question: q })}
                          tooltip={`${c.fullName} · ${q.questionText || `#${q.questionId}`}: ${
                            score == null ? "no data" : score.toFixed(1)
                          }`}
                          disabled={!onCellClick}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Per-question scores come from candidate_video_score_t. That table stores
// scores on a 0-10 scale (not 0-100 like the overall band), so the bands
// are different from the skill heatmap.
const BAND_STYLE = (score) => {
  if (score == null) return { bg: "#F8FAFC", fg: "#94A3B8" };
  const s = Number(score);
  if (s >= 8) return { bg: "#10B981", fg: "#FFFFFF" };
  if (s >= 6) return { bg: "#6EE7B7", fg: "#064E3B" };
  if (s >= 4) return { bg: "#FBBF24", fg: "#78350F" };
  if (s >= 2) return { bg: "#F97316", fg: "#FFFFFF" };
  return { bg: "#EF4444", fg: "#FFFFFF" };
};

function Cell({ score, onClick, tooltip, disabled }) {
  const { bg, fg } = BAND_STYLE(score);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || score == null}
      title={tooltip}
      className="text-sm font-semibold tabular-nums leading-none flex items-center justify-center w-[calc(100%-4px)]"
      style={{
        background: bg,
        color: fg,
        height: 36,
        margin: 2,
        borderRadius: 4,
        cursor: disabled || score == null ? "default" : "pointer",
        border: "none",
      }}
    >
      {score == null ? "—" : score.toFixed(1)}
    </button>
  );
}
